import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from 'lib/request/api';
import { subscribe } from 'lib/pusher';
import { useEval3Page } from './context';
import {
  carryMembershipKey,
  carryTermsKey,
  carryTriggerKey,
  isCarryItemComplete,
  isPreviewSuccess,
  type CarrySeriesPoint,
  type ChartView,
  type InterestPreviewDropResponse,
  type InterestPreviewResponse,
  type TractPreviewResult,
} from './interest-modeling-preview';
import type { CalculateRequest, CarryWell, InterestItem } from './types';

// The preview reprice is a lightweight per-edit call, but it runs the calculator; use
// the eval3 feature-standard long timeout so a slow reprice degrades to a soft error
// (kept last-good) rather than ky's 30s default cutting a legitimate reprice short.
const PREVIEW_TIMEOUT_MS = 300_000;

// A DRAFT eval (`eval/new`) has no EvaluationCalculation row, so no member route —
// its previews go to the draft endpoints, carrying the draft's working config
// (serialized by the same buildCalculateRequest Calculate uses) in the body.
const previewPath = (calculationId: number | null) => {
  return calculationId === null
    ? 'evaluations/draft_interest_preview'
    : `evaluations/calculations/${calculationId}/interest_preview`;
};
const dropPath = (calculationId: number | null) => {
  return calculationId === null
    ? 'evaluations/draft_interest_preview_drop'
    : `evaluations/calculations/${calculationId}/interest_preview_drop`;
};

/**
 * Preview session status.
 *   idle                — nothing (yet) to show.
 *   preparing           — Case-1 async frame build in flight (Pusher-gated); editing stays live.
 *   ready               — at least one tract's preview is cached.
 *   needs_recalculation — the committed calc predates the pairs sidecar; prompt a recalc.
 *   error               — the last reprice soft-failed; the last-good preview is retained.
 */
export type PreviewStatus =
  'idle' | 'preparing' | 'ready' | 'needs_recalculation' | 'error';

export interface CarryPreview {
  /** Server-recombined cross-tract portfolio — drives the KPIs + Aggregate chart. */
  portfolio: CarrySeriesPoint[];
  /** Per-tract last-good payloads — drive the summary table + scoped charts. */
  cache: ReadonlyMap<string, TractPreviewResult>;
  status: PreviewStatus;
  /**
   * Tracts with a reprice in flight. A reprice of an already-cached tract keeps `status`
   * 'ready' with the last-good preview showing, so this is the surface's only signal of
   * activity. Mirrors the hook's in-flight de-dupe map exactly (a superseded response
   * leaves its tract busy — the newer dispatch already re-armed it).
   */
  repricingTractIds: ReadonlySet<string>;
  /** Reprice one carry item (the on-blur trigger); gated on completeness + de-duped. */
  reprice: (item: InterestItem, view: ChartView) => void;
  /** Drop a removed tract from the session (evicts its cache + recombines the rest). */
  drop: (tractId: string) => void;
}

export interface UseCarryPreviewArgs {
  /** The working config's interest items (carry ones are warmed automatically). */
  interestItems: InterestItem[];
  /** The active chart view (byProperty opts the active tract into the heavy slots[] fetch). */
  view: ChartView;
  /** The tract the content area is focused on (scopes byProperty's on-demand slots fetch). */
  activeTractId: string | null;
  /**
   * Each tract's full well roster (from `useTractWells`). The reprice attaches it as the
   * item's `wellIds` so the calculator values wells left out of every group as a full
   * working interest. Optional (defaults to none) so callers/tests without a roster keep
   * the groups-only behavior.
   */
  wellsByTract?: ReadonlyMap<string, CarryWell[]>;
  /**
   * True while the roster fetch is in flight. Gates the warm drain: a tract warmed
   * before the roster lands is priced groups-only (no unassigned-well valuation), and
   * only the ACTIVE tract heals on roster arrival — every other cached tract would keep
   * its roster-less result inside the recombined portfolio until visited. Gate ONLY
   * while in flight: if the fetch errors or yields no roster this goes false and the
   * drain proceeds roster-less, so a failed roster fetch never permanently blocks the
   * preview.
   */
  rosterLoading?: boolean;
  /**
   * DRAFT (row-less) evals only: builds the draft's Calculate-shaped request base —
   * the SAME serializer Calculate uses (`buildCalculateRequest`, at the callsite so
   * the form coupling stays there) — which the draft endpoints normalize into the
   * in-memory calculation they preview against. Present ⇒ the null-calculationId
   * skip is lifted and requests go to the draft endpoints; absent on a draft ⇒ the
   * hook stays idle (previews need the draft context). Ignored for a real eval.
   */
  buildDraftRequest?: () => CalculateRequest;
  /**
   * DRAFT evals only: a stable signature of the PRICING-RELEVANT draft config
   * (effective date / scenario / case / deck / overrides — NOT the interest items,
   * whose changes the per-tract terms keys already handle). A committed calc's
   * config is a frozen snapshot, but a draft's is the live form — when it changes,
   * every cached tract and the session's prepared frame were priced under the OLD
   * config, so the hook resets the whole preview session (token, caches, latches)
   * and re-warms under the new one. Also the recovery path for a warm that 422'd
   * on a not-yet-valid draft (e.g. no scenario picked yet): fixing the config
   * changes the signature and lifts the warm latch. Ignored for a real eval.
   */
  draftSessionKey?: string;
}

/** Per-tract request identity for de-dupe + slots-need tracking. */
interface RequestKey {
  terms: string;
  need: 'light' | 'full';
}

// The reprice payload item: the tract's full well roster attached as the item's top-level
// `wellIds`, so the calculator can value wells left out of every group ("unassigned") as
// a full working interest. With no roster (election items, roster not yet loaded) the
// item goes as-is — the groups-only behavior.
function withRoster(
  item: InterestItem,
  roster: CarryWell[] | undefined
): InterestItem {
  return roster && roster.length > 0
    ? { ...item, wellIds: roster.map((well) => well.id) }
    : item;
}

/**
 * Owns the ephemeral interest-preview lifecycle for the carry portfolio.
 * Redis-free: a server-minted token is held + echoed on every call.
 *
 * State machine, per tract:
 *   • warm — on add / after seed, each COMPLETE carry tract is repriced once (light)
 *     so the portfolio KPIs + aggregate are complete without visiting each tract. The
 *     warm effect drains uncached tracts ONE at a time (the BE recombine is order-
 *     sensitive, so the last response must see every blob).
 *   • reprice — the active tract's terms change + a field blurs → reprice (gated on
 *     completeness; de-duped by a terms+slots key; a newer edit SUPERSEDES a stale
 *     in-flight response via a per-tract seq). Edits that emit no blur (group structure,
 *     payout metric) reprice through the no-blur effect below instead.
 *   • byProperty — switching to the byProperty view fetches the active tract's slots[]
 *     on demand (aggregate/byAsset never request slots).
 *   • drop — remove → interest_preview_drop → evict the tract + recombine.
 *   • Case-1 — a fresh package returns needs_prepare/preparing → show "preparing…",
 *     subscribe to Pusher `interest_preview_<token>`, and on `preview_prepared` re-drain
 *     (frame now exists → results); `preview_prepare_failed` → quiet error, retry on edit.
 *   • never hard-fail — a soft error keeps the last-good preview + surfaces `error`.
 *
 * @param {UseCarryPreviewArgs} args - The interest items, active view, and focused tract.
 * @return {CarryPreview} The portfolio state + reprice/drop actions.
 */
export function useCarryPreview({
  interestItems,
  view,
  activeTractId,
  wellsByTract,
  rosterLoading = false,
  buildDraftRequest,
  draftSessionKey,
}: UseCarryPreviewArgs): CarryPreview {
  const { calculationId } = useEval3Page();
  // A preview target exists when the eval has a row (member routes) or the caller
  // supplied the draft context (draft endpoints). Without either, stay idle.
  const previewEnabled =
    calculationId !== null || buildDraftRequest !== undefined;
  // Ref so reprice/drop read the latest builder at dispatch time without taking it
  // as a dependency (same pattern as wellsByTractRef below).
  const buildDraftRequestRef = useRef(buildDraftRequest);
  buildDraftRequestRef.current = buildDraftRequest;
  const [portfolio, setPortfolio] = useState<CarrySeriesPoint[]>([]);
  const [cache, setCache] = useState<Map<string, TractPreviewResult>>(
    () => new Map()
  );
  const [status, setStatus] = useState<PreviewStatus>('idle');
  // Bumped on `preview_prepared` to re-run the warm drain once the Case-1 frame exists.
  const [prepareEpoch, setPrepareEpoch] = useState(0);

  // Control plane (refs — not render state).
  const tokenRef = useRef<string | null>(null);
  const seqRef = useRef<Map<string, number>>(new Map());
  const inFlightRef = useRef<Map<string, RequestKey>>(new Map());
  // The render-state mirror of `inFlightRef`'s keys, for the surface's busy indicator. The
  // ref stays the source of truth (reprice reads it synchronously to de-dupe); every
  // per-tract mutation of the ref goes through markInFlight / clearInFlight so the mirror
  // can't drift from it.
  const [repricingTractIds, setRepricingTractIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const markInFlight = useCallback((tractId: string, key: RequestKey) => {
    inFlightRef.current.set(tractId, key);
    setRepricingTractIds((prev) => {
      if (prev.has(tractId)) {
        return prev;
      }
      return new Set(prev).add(tractId);
    });
  }, []);
  const clearInFlight = useCallback((tractId: string) => {
    inFlightRef.current.delete(tractId);
    setRepricingTractIds((prev) => {
      if (!prev.has(tractId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(tractId);
      return next;
    });
  }, []);
  const lastKeyRef = useRef<Map<string, RequestKey>>(new Map());
  // The terms a tract's automatic warm was last attempted for — so a soft-errored /
  // preparing warm doesn't re-fire in a loop (an explicit reprice/edit still retries).
  const warmAttemptRef = useRef<Map<string, string>>(new Map());
  // Same loop guard for the byProperty on-demand slots fetch: the terms it was last
  // attempted for per tract, so a response that (contrary to contract) omits slots[]
  // can't re-fire the effect every render. A terms change (edit) re-attempts.
  const slotsAttemptRef = useRef<Map<string, string>>(new Map());
  // Monotonic portfolio generation, bumped on every drop. A reprice captures it at
  // dispatch and only writes the recombined portfolio if no drop has happened since — so
  // a reprice recombined server-side BEFORE a concurrent drop can't overwrite the
  // post-drop portfolio with an aggregate that still includes the dropped tract.
  const portfolioGenRef = useRef(0);
  // The tract roster, held in a ref so reprice can read the latest without taking it as a
  // dependency (which would rebuild reprice — and re-run the warm effect — on every roster
  // identity change). Attached to the reprice payload as `wellIds` so the calculator can
  // value unassigned wells as a full working interest.
  const wellsByTractRef = useRef(wellsByTract);
  wellsByTractRef.current = wellsByTract;

  // Draft-config invalidation: a pricing-relevant change to the LIVE draft config
  // obsoletes the whole session — the prepared frame and every cached tract carry
  // the old config's prices (and mixing them with post-change reprices would show
  // an inconsistent portfolio). Reset to a fresh session: drop the token (a new
  // prepare is dispatched under a new one), supersede in-flight responses, clear
  // the caches + de-dupe/warm latches, and let the warm drain start over. Skipped
  // on mount and on real evals (the key is undefined and never changes there).
  const prevDraftKeyRef = useRef(draftSessionKey);
  useEffect(() => {
    if (prevDraftKeyRef.current === draftSessionKey) {
      return;
    }
    prevDraftKeyRef.current = draftSessionKey;
    tokenRef.current = null;
    seqRef.current.forEach((seq, tract) => seqRef.current.set(tract, seq + 1));
    inFlightRef.current.clear();
    setRepricingTractIds(new Set());
    lastKeyRef.current.clear();
    warmAttemptRef.current.clear();
    slotsAttemptRef.current.clear();
    portfolioGenRef.current += 1;
    setCache(new Map());
    setPortfolio([]);
    setStatus('idle');
    setPrepareEpoch((epoch) => epoch + 1);
  }, [draftSessionKey]);

  const reprice = useCallback(
    (item: InterestItem, activeView: ChartView) => {
      if (!previewEnabled || !isCarryItemComplete(item)) {
        return;
      }
      const tractId = item.tractId;
      // Carry the tract's full roster so the calculator can price wells left out of every
      // group as a full working interest. The de-dupe key is computed from the PAYLOAD
      // item, so the roster is part of the request identity — a payload differing only in
      // roster (the unassigned set changed) is never skipped as a duplicate.
      const payloadItem = withRoster(
        item,
        wellsByTractRef.current?.get(tractId)
      );
      const terms = carryTermsKey(payloadItem);
      const need: RequestKey['need'] =
        activeView === 'byProperty' ? 'full' : 'light';
      const satisfies = (stored: RequestKey | undefined) =>
        !!stored &&
        stored.terms === terms &&
        (need === 'light' || stored.need === 'full');
      if (
        satisfies(inFlightRef.current.get(tractId)) ||
        satisfies(lastKeyRef.current.get(tractId))
      ) {
        return;
      }

      markInFlight(tractId, { terms, need });
      const mySeq = (seqRef.current.get(tractId) ?? 0) + 1;
      seqRef.current.set(tractId, mySeq);
      const myGen = portfolioGenRef.current;

      void (async () => {
        try {
          const response = await api
            .post(previewPath(calculationId), {
              json: {
                // A draft carries its Calculate-shaped working config so the BE can
                // assemble the in-memory calculation it previews against.
                ...(calculationId === null
                  ? buildDraftRequestRef.current?.()
                  : undefined),
                tractId,
                item: payloadItem,
                token: tokenRef.current ?? undefined,
                view: activeView,
              },
              timeout: PREVIEW_TIMEOUT_MS,
            })
            .json<InterestPreviewResponse>();

          if (seqRef.current.get(tractId) !== mySeq) {
            return; // superseded by a newer edit
          }
          tokenRef.current = response.token ?? tokenRef.current;
          clearInFlight(tractId);

          if (!isPreviewSuccess(response)) {
            if (response.status === 'error') {
              setStatus('error'); // soft — keep last-good
            } else if (response.status === 'needs_recalculation') {
              setStatus('needs_recalculation'); // old calc — prompt a recalc
            } else {
              setStatus('preparing'); // Case-1 needs_prepare / preparing
            }
            return;
          }

          const slots = response.tract.slots;
          const hadSlots = Array.isArray(slots);
          setCache((prev) =>
            new Map(prev).set(tractId, {
              groups: response.tract.groups ?? [],
              slots,
              aggregate: response.tract.aggregate ?? [],
            })
          );
          // Skip the portfolio write if a drop bumped the generation since dispatch —
          // this response's recombine may predate that drop and re-include the tract.
          if (myGen === portfolioGenRef.current) {
            setPortfolio(response.aggregate ?? []);
          }
          setStatus('ready');
          lastKeyRef.current.set(tractId, {
            terms,
            need: hadSlots ? 'full' : 'light',
          });
        } catch {
          if (seqRef.current.get(tractId) !== mySeq) {
            return;
          }
          clearInFlight(tractId);
          setStatus('error'); // soft — never block editing
        }
      })();
    },
    [calculationId, previewEnabled, markInFlight, clearInFlight]
  );

  const drop = useCallback(
    (tractId: string) => {
      // Evict local state immediately + supersede any in-flight reprice for the tract
      // (its late response must not re-add it), then tell the server to recombine.
      seqRef.current.set(tractId, (seqRef.current.get(tractId) ?? 0) + 1);
      clearInFlight(tractId);
      lastKeyRef.current.delete(tractId);
      warmAttemptRef.current.delete(tractId);
      slotsAttemptRef.current.delete(tractId);
      // Invalidate any in-flight reprice's portfolio write (it may predate this drop).
      portfolioGenRef.current += 1;
      const myGen = portfolioGenRef.current;
      setCache((prev) => {
        const next = new Map(prev);
        next.delete(tractId);
        return next;
      });
      if (!previewEnabled) {
        return;
      }
      void api
        .post(dropPath(calculationId), {
          json: { tractId, token: tokenRef.current ?? undefined },
          timeout: PREVIEW_TIMEOUT_MS,
        })
        .json<InterestPreviewDropResponse>()
        .then((response) => {
          tokenRef.current = response.token ?? tokenRef.current;
          if (myGen === portfolioGenRef.current) {
            setPortfolio(response.aggregate ?? []);
          }
        })
        .catch(() => {
          // Soft: the deferred TTL sweep + the next reprice's recombine self-heal.
        });
    },
    [calculationId, previewEnabled, clearInFlight]
  );

  // Warm drain: reprice the first complete, uncached, not-in-flight carry tract (light),
  // once per terms. Sequential (one per pass) so the recombine race can't drop a tract.
  // Held while the roster fetch is in flight (`rosterLoading`) so every warmed tract is
  // priced WITH its roster — see the arg doc for why, and why a failed fetch can't
  // block the drain.
  useEffect(() => {
    if (
      !previewEnabled ||
      rosterLoading ||
      status === 'preparing' ||
      status === 'needs_recalculation'
    ) {
      return;
    }
    for (const item of interestItems) {
      if (item.kind !== 'carry') {
        continue;
      }
      const tractId = item.tractId;
      if (cache.has(tractId) || inFlightRef.current.has(tractId)) {
        continue;
      }
      if (!isCarryItemComplete(item)) {
        continue;
      }
      const terms = carryTermsKey(item);
      if (warmAttemptRef.current.get(tractId) === terms) {
        continue; // already warmed this terms — no auto-retry loop
      }
      warmAttemptRef.current.set(tractId, terms);
      reprice(item, 'aggregate');
      break;
    }
  }, [
    interestItems,
    cache,
    status,
    prepareEpoch,
    previewEnabled,
    rosterLoading,
    reprice,
  ]);

  // Immediate reprice on an edit to the active carry tract that emits NO field blur, so
  // the section's on-blur reprice never fires for it and the chart/KPIs/summary keep
  // showing the old terms: a group-STRUCTURE change (a group removed, or a well unchecked
  // to unassigned / moved between groups — button / multi-select edits), or a payout-
  // METRIC change (the Dropdown's menu keeps focus on its toggle, so no focusout bubbles).
  // Keyed on a membership + per-group-trigger signature so a WI/NRI/threshold edit (which
  // DOES blur) is a no-op here — no per-keystroke or per-slider-step churn. The membership
  // half is computed on the roster-ATTACHED item (and the memo watches `wellsByTract`), so
  // a roster that loads after the tract is cached also triggers a reprice — pulling the
  // unassigned-well valuation into the preview. Only for an already-cached tract (the warm
  // drain covers the first reprice), and the reprice call de-dupes a redundant fire.
  const activeNoBlurKey = useMemo(() => {
    const item = interestItems.find(
      (candidate) =>
        candidate.tractId === activeTractId && candidate.kind === 'carry'
    );
    if (!item) {
      return null;
    }
    const membership = carryMembershipKey(
      withRoster(item, wellsByTract?.get(item.tractId))
    );
    return `${membership}|triggers:${carryTriggerKey(item)}`;
  }, [interestItems, activeTractId, wellsByTract]);
  const prevNoBlurKeyRef = useRef(activeNoBlurKey);
  useEffect(() => {
    if (prevNoBlurKeyRef.current === activeNoBlurKey) {
      return;
    }
    // Only advance the marker once we actually dispatch. If the tract isn't cached yet (its
    // first reprice is still in flight) the change stays PENDING and reprices the moment the
    // tract caches — otherwise a structure change during the warm window would be dropped.
    if (
      !previewEnabled ||
      activeTractId === null ||
      !cache.has(activeTractId)
    ) {
      return;
    }
    const item = interestItems.find(
      (candidate) =>
        candidate.tractId === activeTractId && candidate.kind === 'carry'
    );
    if (!item || !isCarryItemComplete(item)) {
      return;
    }
    prevNoBlurKeyRef.current = activeNoBlurKey;
    reprice(item, view);
  }, [
    activeNoBlurKey,
    activeTractId,
    cache,
    interestItems,
    view,
    previewEnabled,
    reprice,
  ]);

  // byProperty on demand: fetch the active tract's slots[] when it's the active view
  // and they aren't cached yet (aggregate/byAsset never request slots).
  useEffect(() => {
    if (
      !previewEnabled ||
      status === 'preparing' ||
      status === 'needs_recalculation' ||
      view !== 'byProperty' ||
      activeTractId === null
    ) {
      return;
    }
    const item = interestItems.find(
      (candidate) =>
        candidate.tractId === activeTractId && candidate.kind === 'carry'
    );
    if (!item) {
      return;
    }
    const entry = cache.get(activeTractId);
    if (entry && Array.isArray(entry.slots)) {
      return; // slots already cached
    }
    const terms = carryTermsKey(item);
    if (slotsAttemptRef.current.get(activeTractId) === terms) {
      return; // already attempted slots for these terms — no re-fire loop
    }
    slotsAttemptRef.current.set(activeTractId, terms);
    reprice(item, 'byProperty');
  }, [
    view,
    activeTractId,
    interestItems,
    cache,
    status,
    previewEnabled,
    reprice,
  ]);

  // Case-1 async prepare: while preparing, subscribe to the preview channel; on
  // `preview_prepared` re-drain the warm loop (frame now exists), on failure surface a
  // quiet error (editing stays live; the next edit retries). Keyed on `status` only —
  // the token is stable for the whole preparing spell (a reprice echoes tokenRef, so the
  // BE keeps the same token), so tokenRef.current is the right channel when this runs.
  useEffect(() => {
    if (status !== 'preparing') {
      return undefined;
    }
    const token = tokenRef.current;
    if (!token) {
      return undefined;
    }
    const channel = subscribe(`interest_preview_${token}`);
    const onPrepared = () => {
      warmAttemptRef.current.clear();
      slotsAttemptRef.current.clear();
      setStatus('idle');
      setPrepareEpoch((epoch) => epoch + 1);
    };
    const onFailed = () => setStatus('error');
    channel.bind('preview_prepared', onPrepared);
    channel.bind('preview_prepare_failed', onFailed);
    return () => {
      channel.unbind('preview_prepared', onPrepared);
      channel.unbind('preview_prepare_failed', onFailed);
      channel.unsubscribe();
    };
  }, [status]);

  return { portfolio, cache, status, repricingTractIds, reprice, drop };
}
